import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { Icon } from "../components/Icon.tsx";
import { Avatar, Empty, ErrorView, Field, Loading, Page, Sheet, useFail, useToast } from "../components/ui.tsx";
import { api, isQueued } from "../lib/api.ts";
import { dayLabel, label, phone, time, when } from "../lib/format.ts";
import { useMe } from "../lib/session.tsx";

const FILTERS = [["needs_reply", "Needs reply"], ["mine", "Mine"], ["unassigned", "Unassigned"], ["all", "All"]] as const;

export function Inbox() {
  const [sp, setSp] = useSearchParams();
  const filter = sp.get("filter") ?? "needs_reply";
  const status = sp.get("status") ?? "open";
  const channel = sp.get("channel") ?? "";
  const qs = new URLSearchParams({ filter, status, ...(channel ? { channel } : {}) }).toString();
  const q = useQuery({ queryKey: ["conversations", qs], queryFn: () => api.get<any[]>(`/conversations?${qs}`), refetchInterval: 30_000 });
  const set = (k: string, v: string) => { const n = new URLSearchParams(sp); v ? n.set(k, v) : n.delete(k); setSp(n, { replace: true }); };
  return (
    <Page title="Inbox">
      <div className="chips">
        {FILTERS.map(([k, l]) => <button key={k} className={"chip" + (filter === k ? " on" : "")} onClick={() => set("filter", k)}>{l}</button>)}
        <button className={"chip" + (status !== "open" ? " on" : "")} onClick={() => set("status", status === "open" ? "closed" : status === "closed" ? "snoozed" : "open")}>
          {status === "open" ? "Open" : status === "closed" ? "Closed" : "Snoozed"}
        </button>
        <select className="chip" value={channel} onChange={(e) => set("channel", e.target.value)} aria-label="Channel">
          <option value="">Every channel</option>
          {["sms", "call", "email", "portal_enquiry", "webchat", "form", "facebook", "instagram"].map((c) => <option key={c} value={c}>{label(c)}</option>)}
        </select>
      </div>
      {q.isLoading ? <Loading /> : q.error ? <ErrorView error={q.error} /> : q.data!.length === 0 ? <Empty>Nothing here.</Empty> : (
        <div className="list">
          {q.data!.map((c) => (
            <Link key={c.id} to={`/inbox/${c.id}`} className={"row" + (c.unread_count ? " unread" : "")}>
              <Avatar name={c.name} blue={c.needs_reply} />
              <div className="main-col">
                <div className="title">{c.name}</div>
                <div className="sub">{c.last_direction === "outbound" ? "You: " : ""}{c.last_status === "draft" ? "Draft: " : ""}{c.preview}</div>
                <div className="sub dim" style={{ fontSize: 12 }}>{label(c.last_channel)}{c.listing_address ? ` · ${c.listing_address}` : ""}{c.assigned_name ? ` · ${c.assigned_name}` : " · Unassigned"}</div>
              </div>
              <span className="when">{when(c.last_at)}</span>
            </Link>
          ))}
        </div>
      )}
    </Page>
  );
}

export function Thread() {
  const { id } = useParams();
  const [sp] = useSearchParams();
  const me = useMe();
  const qc = useQueryClient();
  const toast = useToast();
  const fail = useFail();
  const q = useQuery({ queryKey: ["conversation", id], queryFn: () => api.get(`/conversations/${id}`), refetchInterval: 20_000 });
  const users = useQuery({ queryKey: ["users"], queryFn: () => api.get<any[]>("/users") });
  const [mode, setMode] = useState<"sms" | "email" | "internal">(sp.get("mode") === "email" ? "email" : "sms");
  const [body, setBody] = useState("");
  const [subject, setSubject] = useState("");
  const [sheet, setSheet] = useState<null | "snooze" | "assign" | "call">(null);
  const end = useRef<HTMLDivElement>(null);
  const c = q.data;

  useEffect(() => { end.current?.scrollIntoView({ block: "end" }); }, [c?.messages?.length]);
  useEffect(() => {
    if (c?.unread_count) api.patch(`/conversations/${id}`, { read: true }).then(() => qc.invalidateQueries({ queryKey: ["inbox-count"] })).catch(() => {});
  }, [c?.id, c?.unread_count]);
  useEffect(() => { if (c && !subject) { const last = [...c.messages].reverse().find((m: any) => m.subject); if (last) setSubject(last.subject.startsWith("Re:") ? last.subject : `Re: ${last.subject}`); } }, [c?.id]);

  const refresh = () => { qc.invalidateQueries({ queryKey: ["conversation", id] }); qc.invalidateQueries({ queryKey: ["conversations"] }); qc.invalidateQueries({ queryKey: ["today"] }); qc.invalidateQueries({ queryKey: ["inbox-count"] }); };
  const patch = async (b: object, msg: string) => {
    try { await api.patch(`/conversations/${id}`, b); toast(msg); setSheet(null); refresh(); } catch (e) { fail(e); }
  };
  const submit = async () => {
    if (!body.trim()) return;
    try {
      const r: any = await api.post(`/conversations/${id}/messages`, { channel: mode, body, ...(mode === "email" ? { subject } : {}), listing_id: c?.listing_id ?? null },
        { offline: mode === "internal" ? "Add a note to the thread" : `Draft a ${label(mode).toLowerCase()} to ${c?.display_name}` });
      setBody("");
      toast(isQueued(r) ? "Saved on this phone. It'll sync when you're back in range." : r.draft ? "Draft saved, waiting for approval. Nothing was sent." : "Note added");
      refresh();
    } catch (e) { fail(e); }
  };

  const groups = useMemo(() => {
    const out: { day: string; items: any[] }[] = [];
    for (const m of c?.messages ?? []) {
      const d = dayLabel(m.occurred_at);
      if (!out.length || out[out.length - 1].day !== d) out.push({ day: d, items: [] });
      out[out.length - 1].items.push(m);
    }
    return out;
  }, [c?.messages]);

  if (q.isLoading) return <Page title="Thread" back="/inbox"><Loading /></Page>;
  if (q.error || !c) return <Page title="Thread" back="/inbox"><ErrorView error={q.error} /></Page>;
  const name = c.display_name ?? `${c.partial_name ?? "Website visitor"} (not matched yet)`;
  const consent = c.consent;
  const blocked = mode !== "internal" && consent && !consent[mode]?.service;
  const sendingLabel = mode === "internal" ? "Add note" : "Save draft";

  return (
    <Page title={c.person_id ? <Link to={`/people/${c.person_id}`}>{name}</Link> : name} back="/inbox" actions={
      <>
        {c.person_id && <button className="iconbtn" aria-label="Log a call" onClick={() => setSheet("call")}><Icon name="phone" /></button>}
        <button className="iconbtn" aria-label="Snooze" onClick={() => setSheet("snooze")}><Icon name="log" /></button>
        <button className="iconbtn" aria-label={c.status === "closed" ? "Reopen" : "Close"} onClick={() => patch({ status: c.status === "closed" ? "open" : "closed" }, c.status === "closed" ? "Reopened" : "Closed")}>
          <Icon name={c.status === "closed" ? "undo" : "check"} />
        </button>
      </>
    }>
      <div className="chips" style={{ paddingBottom: 0 }}>
        <button className="chip" onClick={() => setSheet("assign")}><Icon name="user" size={15} />{c.assigned_name ?? "Unassigned"}</button>
        {c.status !== "open" && <span className="badge">{c.status}</span>}
        {c.handles?.slice(0, 2).map((h: any) => <span key={h.value_norm} className="badge handle">{h.display}</span>)}
      </div>

      <div className="thread">
        {groups.map((g) => (
          <Fragment key={g.day}>
            <div className="daysep">{g.day}</div>
            {g.items.map((m: any) => {
              const kind = m.direction === "internal" ? "internal" : m.channel === "call" || m.channel === "voicemail" ? "call" : m.status === "draft" ? "draft" : m.direction === "outbound" ? "out" : "in";
              return (
                <div key={m.id} className={"bubble " + kind}>
                  {m.about && kind !== "internal" && <div className="about">{m.about}</div>}
                  {m.subject && <div className="subj">{m.subject}</div>}
                  {m.body_text}
                  {m.transcript && <div style={{ marginTop: 6, fontSize: 13 }}>“{m.transcript}”</div>}
                  {m.summary && kind === "call" && <div style={{ marginTop: 6, fontSize: 13 }}>{m.summary}</div>}
                  <div className="meta">
                    <span>{label(m.channel)}</span><span>{time(m.occurred_at)}</span>
                    {m.sent_by_name && m.direction !== "inbound" && <span>{m.sent_by_name}</span>}
                    {m.duration_s != null && <span>{Math.round(m.duration_s / 60)} min</span>}
                    {kind === "draft" && <span className="badge blue">Draft · waiting for approval</span>}
                  </div>
                </div>
              );
            })}
          </Fragment>
        ))}
        <div ref={end} style={{ scrollMarginBottom: 200 }} />
      </div>

      <div className="composer">
        <div className="modes">
          {(["sms", "email", "internal"] as const).map((m) => <button key={m} className={"chip" + (mode === m ? " on" : "")} onClick={() => setMode(m)}>{m === "internal" ? "Team note" : label(m)}</button>)}
        </div>
        {mode === "email" && <input className="input" style={{ marginBottom: 8 }} value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Subject" />}
        <div className="line">
          <textarea value={body} onChange={(e) => setBody(e.target.value)} rows={1} placeholder={blocked ? "Opted out on this channel" : mode === "internal" ? "Note for the team" : `Reply by ${label(mode).toLowerCase()}`} disabled={!!blocked} />
          <button className="btn" onClick={submit} disabled={!body.trim() || !!blocked || (mode === "email" && !subject)}>{sendingLabel}</button>
        </div>
        <div className="note">
          {blocked ? <><span className="sq" style={{ background: "var(--danger)" }} />This person has opted out or has no {mode === "sms" ? "mobile" : "email"} on file.</>
            : mode === "internal" ? <>Only the team sees notes.</>
            : <><span className="sq" />Sending is off in this build: replies are saved as drafts waiting for approval{mode === "sms" && consent?.sms?.to ? ` (to ${phone(consent.sms.to)})` : ""}.</>}
        </div>
      </div>

      {sheet === "snooze" && (
        <Sheet title="Snooze until" onClose={() => setSheet(null)}>
          {[["In 3 hours", 3], ["Tomorrow morning", 24], ["Next week", 24 * 7]].map(([l, h]) => {
            const until = new Date(Date.now() + (h as number) * 3600_000);
            if ((h as number) >= 24) until.setHours(8, 0, 0, 0);
            return <button key={l as string} className="row" onClick={() => patch({ status: "snoozed", snoozed_until: until.toISOString() }, `Snoozed until ${until.toLocaleString("en-AU", { weekday: "short", hour: "numeric", minute: "2-digit" })}`)}>
              <div className="main-col"><div className="title">{l}</div></div></button>;
          })}
        </Sheet>
      )}
      {sheet === "assign" && (
        <Sheet title="Assign to" onClose={() => setSheet(null)}>
          {(users.data ?? []).filter((u) => me.user.role !== "agent" || u.id === me.user.id).map((u) => (
            <button key={u.id} className="row" onClick={() => patch({ assigned_user_id: u.id }, `Assigned to ${u.name}`)}>
              <Avatar name={u.name} sm /><div className="main-col"><div className="title">{u.name}</div><div className="sub">{label(u.role)}</div></div>
              {c.assigned_user_id === u.id && <Icon name="check" className="blue" />}
            </button>
          ))}
          {me.user.role !== "agent" && <button className="row" onClick={() => patch({ assigned_user_id: null }, "Unassigned")}><div className="main-col"><div className="title">Nobody</div></div></button>}
        </Sheet>
      )}
      {sheet === "call" && c.person_id && <LogCallSheet personId={c.person_id} name={name} onClose={() => setSheet(null)} onDone={refresh} />}
    </Page>
  );
}

export function LogCallSheet({ personId, name, onClose, onDone }: { personId: string; name: string; onClose: () => void; onDone: () => void }) {
  const toast = useToast();
  const fail = useFail();
  const [outcome, setOutcome] = useState("spoke");
  const [direction, setDirection] = useState("outbound");
  const [mins, setMins] = useState("5");
  const [note, setNote] = useState("");
  const [follow, setFollow] = useState("");
  const save = async () => {
    try {
      const r = await api.post("/calls/log", { person_id: personId, direction, outcome, duration_s: outcome === "spoke" ? Math.round(Number(mins) * 60) || undefined : undefined,
        note: note || undefined, ...(follow ? { follow_up_on: follow } : {}) }, { offline: `Log a call with ${name}` });
      toast(isQueued(r) ? "Call saved on this phone. It'll sync when you're back in range." : "Call logged");
      onDone(); onClose();
    } catch (e) { fail(e); }
  };
  return (
    <Sheet title={`Log a call with ${name}`} onClose={onClose} foot={<><button className="btn ghost" onClick={onClose}>Cancel</button><button className="btn" onClick={save}>Log call</button></>}>
      <div className="chips" style={{ padding: "0 0 12px" }}>
        {[["outbound", "I called"], ["inbound", "They called"]].map(([k, l]) => <button key={k} className={"chip" + (direction === k ? " on" : "")} onClick={() => setDirection(k)}>{l}</button>)}
      </div>
      <div className="grid2">
        <Field label="Outcome">
          <select className="select" value={outcome} onChange={(e) => setOutcome(e.target.value)}>
            <option value="spoke">Spoke</option><option value="no_answer">No answer</option><option value="left_voicemail">Left voicemail</option>
            <option value="busy">Busy</option><option value="wrong_number">Wrong number</option>
          </select>
        </Field>
        <Field label="Minutes"><input className="input" inputMode="numeric" value={mins} onChange={(e) => setMins(e.target.value)} disabled={outcome !== "spoke"} /></Field>
      </div>
      <Field label="What was said"><textarea className="textarea" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Optional" /></Field>
      <Field label="Follow up on (makes a task)"><input className="input" type="date" value={follow} onChange={(e) => setFollow(e.target.value)} /></Field>
    </Sheet>
  );
}
